import React, { useEffect, useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import type { Deliverable, Project } from '../../types/portal';
import { waitingLabel } from '../../utils/deliverables';
import { dismissDriveLinkProblem, fetchDriveLinkProblems, driveProblemConsequence, driveProblemHeadline, type DriveLinkProblem } from '../../utils/driveLinkHealth';
import { fetchStalledTranscripts, stalledReason, type StalledTranscript } from '../../utils/transcriptJobs';
import { fetchUnhealthyWorkers, lastRunLabel, workerConsequence, workerName, type WorkerHeartbeat } from '../../utils/workerHealth';
interface Row {
  key: string;
  /** Priority: lower is more actionable. */
  rank: number;
  /** Epoch ms the item started waiting, so the oldest can rise to the top. */
  since?: number;
  /** e.g. "Smart Cities approved" */
  headline: string;
  /** e.g. "BLOOMBERG · V3 · APPROVED BY MORGAN" */
  detail: string;
  /** e.g. "Ready for Portal to publish" — omitted where the row speaks for itself. */
  next?: string;
  /** Where the row goes when pressed. Rows without one render as plain text. */
  onOpen?: () => void;
  /**
   * Clears the row by hand. Only for rows nothing else can clear: a client
   * approval leaves when it is published, but a binned Drive file can be
   * dealt with entirely outside Portal OS, so the studio needs a way to say
   * so. Never set alongside `onOpen` — a button inside a button is invalid.
   */
  onDismiss?: () => void;
}
function detailLine(project: Project, parts: (string | undefined)[]): string {
  return [project.client, ...parts].filter(Boolean).join(' · ');
}

/** Re-checked while the dashboard is left open, quietly. */
const STALLED_REFRESH_MS = 10 * 60 * 1000;

/**
 * **WHAT WAS FETCHED LAST TIME, HELD ACROSS UNMOUNTS. 5 Nov 2026.**
 *
 * Tom: *"when I click to a new tab and go back — the homepage jumps"*, and
 * then: *"it jumps down"*.
 *
 * Three of the four kinds of row below are FETCHED rather than derived — a
 * stopped worker, a stalled transcript, a dead client link — and all three
 * are rank 0, so they sort to the TOP of the list. This component is thrown
 * away when you leave the front page, so coming back drew the derived rows
 * instantly and then, a few hundred milliseconds later, inserted the
 * fetched ones above them. Everything below moved down.
 *
 * That was invisible until the front page became a fixed-height column
 * whose tile grid takes the height left over. Now a row appearing up here
 * resizes all nine photographs.
 *
 * So the three lists are kept in module scope and used as the initial
 * state. Coming back paints what was true a moment ago, at the height it
 * will keep, and the refetch behind it changes nothing unless something
 * actually changed. Module scope dies with the tab, which is the right
 * lifetime: this is a cache for surviving a tab change, not storage.
 *
 * **It is deliberately not in context.** These lists change on a ten-minute
 * timer; putting them in PortalContext would re-render every consumer.
 *
 * The same pattern, for the same reason, is in pages/portal/Week.tsx — read
 * the note there too before changing either.
 */
let lastFetched: {
  stalled: StalledTranscript[];
  unhealthy: WorkerHeartbeat[];
  driveProblems: DriveLinkProblem[];
} | null = null;

/**
 * What the studio has to act on.
 *
 * **It is not there when there is nothing to act on (23 Sep 2026, Tom).** It
 * used to hold its space with a large `Nothing waiting.` and a count set in
 * display type — which made sense while it was the whole top of the page, and
 * stopped making sense the moment the week moved in underneath it. An empty
 * box announcing its own emptiness, at that size, is the biggest thing on the
 * screen on the days when nothing is wrong.
 *
 * So: no box on a clear day, and when it does appear it is a short list at
 * text size with the count in the corner. It is an alarm, and an alarm should
 * be small and silent until it isn't.
 *
 * **It only ever says what is WRONG.** The week sits beside it, and the
 * temptation to let some of that schedule creep in here has to be refused
 * every time: the moment the box carries anything that is merely happening,
 * people stop reading it as "is there anything I have to do?" and start
 * skimming it.
 *
 * Four kinds of row, and each is genuinely someone's move:
 *
 *   0. A background worker has stopped or failed. Rare, and the only thing
 *      standing between a silent breakage and weeks of quietly wrong data.
 *   0. A file behind a client link has gone — binned, unshared, replaced.
 *      Added 6 Oct 2026, after Bloomberg found a broken audio file for us and
 *      the first run of the checker turned up three WFES videos in Drive's
 *      bin on a live project. The studio cannot see what the client sees, so
 *      this is the only place it ever will.
 *   1. A client asked for changes.
 *   2. A client approved something.
 *
 * 1 and 2 are the everyday ones — the reason the box exists at all.
 *
 * **Every row must be clearable (6 Oct 2026, Tom: "I need to be able to clear
 * these though — so they disappear once we are notified").** Most rows clear
 * themselves, because the act that fixes them happens in Portal OS: publish
 * the version, action the changes, and the row goes. The Drive rows are the
 * exception — the fix happens in Google, where this app cannot watch — so
 * they carry a Dismiss of their own. It records that this problem was seen,
 * not that this file is forgiven: a different fault on the same file raises a
 * fresh row, and `prune_drive_link_dismissals()` clears the dismissal once
 * the problem goes, so a link that breaks again later alarms again.
 *
 * **A row must never state a cause it has not established (26 Sep 2026).** The
 * stalled-transcript row used to end "check the scheduled worker is running"
 * on every occurrence, which was a guess printed in the same voice as the
 * facts above it — and it sent Tom to a worker whose schedule was fine. An
 * alarm that names the wrong cause is worse than one that names none: it is
 * believed. `stalledReason()` now distinguishes the three cases it can
 * actually tell apart. Any row added here owes the reader the same.
 *
 * **What was taken out, and why (23 Sep 2026).** A fourth row listed diary
 * entries the calendar worker held back because Tom and Zeena had both typed
 * the same meeting. Tom: "take 4 out, keep the rest." It was worth knowing and
 * it was not a move anyone had to make — and on the day it was removed it was
 * the ONLY thing keeping the box on screen, which is how an alarm teaches
 * people to ignore it. The clash is still plain on the Calendar, where both
 * copies sit on the same square. `fetchDuplicateEntries()` in
 * utils/calendarEntries.ts has no caller now: leave it or delete it, but do
 * not wire it back in here.
 *
 * **This file is at the size where a whole-file write gets silently
 * truncated on this editor** — the same wall `TellThem.tsx` hit on 19 Oct.
 * Read it back after writing. If it needs much more, split the row-building
 * out before adding to it.
 */
export function NeedsAttention() {
  const {
    activeProjects,
    openActivity
  } = usePortal();

  /**
   * Things that are true of the studio rather than of a project: transcript
   * requests the Descript worker never picked up, scheduled workers that have
   * stopped, and client links whose files are no longer where they were.
   *
   * The only things on this dashboard that are fetched rather than derived,
   * and for the same reason: they are facts about the world outside this app
   * entirely. They belong here rather than on a project row because the person
   * who should notice is not necessarily the person whose work is affected.
   *
   * Seeded from `lastFetched` so a return to the front page does not shift
   * the layout — see the note on it above.
   */
  const [stalled, setStalled] = useState<StalledTranscript[]>(() => lastFetched?.stalled ?? []);
  const [unhealthy, setUnhealthy] = useState<WorkerHeartbeat[]>(() => lastFetched?.unhealthy ?? []);
  const [driveProblems, setDriveProblems] = useState<DriveLinkProblem[]>(() => lastFetched?.driveProblems ?? []);
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [jobs, workers, links] = await Promise.all([fetchStalledTranscripts(), fetchUnhealthyWorkers(), fetchDriveLinkProblems()]);
      if (cancelled) return;
      lastFetched = {
        stalled: jobs,
        unhealthy: workers,
        driveProblems: links
      };
      setStalled(jobs);
      setUnhealthy(workers);
      setDriveProblems(links);
    }
    void load();
    const timer = setInterval(() => void load(), STALLED_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  /**
   * Gone from the screen at once, because a dismissal that waits on a round
   * trip feels broken. If the write fails the row comes straight back, which
   * is the honest signal — better than a box that says "saved" when it wasn't.
   *
   * The cache is corrected too, or coming back to the page would resurrect
   * the row from module scope and then remove it again on the refetch.
   */
  async function dismissDriveProblem(problem: DriveLinkProblem) {
    const key = `${problem.linkKey}::${problem.verdict}`;
    const remaining = driveProblems.filter(item => `${item.linkKey}::${item.verdict}` !== key);
    setDriveProblems(remaining);
    if (lastFetched) lastFetched = {
      ...lastFetched,
      driveProblems: remaining
    };
    const saved = await dismissDriveLinkProblem(problem);
    if (!saved) {
      setDriveProblems(current => [...current, problem]);
      if (lastFetched) {
        lastFetched = {
          ...lastFetched,
          driveProblems: [...lastFetched.driveProblems, problem]
        };
      }
    }
  }
  const rows: Row[] = [];
  const push = (project: Project, item: Deliverable, row: Omit<Row, 'key' | 'onOpen'>) => rows.push({
    ...row,
    key: `${item.id}-${row.rank}`,
    onOpen: () => openActivity({
      id: `${item.id}-${row.rank}`,
      projectId: project.id,
      projectName: '',
      actorName: '',
      action: '',
      deliverableId: item.id,
      at: Date.now()
    })
  });

  /* Rank 0: the pipeline itself is broken, which outranks any single piece of
     work waiting inside it. A stopped worker is not a project, so there is
     nothing to open — a button that went nowhere would be worse than text. */
  for (const beat of unhealthy) {
    const failed = beat.status === 'error';
    rows.push({
      key: `worker-${beat.worker}`,
      rank: 0,
      since: beat.lastRunAt,
      headline: failed ? `${workerName(beat.worker)} reported a problem` : `${workerName(beat.worker)} has stopped running`,
      detail: [lastRunLabel(beat), failed ? beat.detail : ''].filter(Boolean).join(' · '),
      next: workerConsequence(beat.worker)
    });
  }

  /* Also rank 0, and for the same reason: a client opening a dead link is not
     a piece of work waiting, it is the studio looking as though it cannot
     keep track of its own files. The fix lives in Drive rather than in Portal
     OS, so these rows open nothing — and carry a Dismiss instead, because
     nothing in this app will ever notice them being fixed by hand. */
  for (const problem of driveProblems) {
    rows.push({
      key: `drive-${problem.linkKey}-${problem.verdict}`,
      rank: 0,
      since: problem.checkedAt,
      headline: driveProblemHeadline(problem),
      detail: [problem.clientName, problem.places > 1 ? `${problem.places} places point at it` : ''].filter(Boolean).join(' · '),
      next: driveProblemConsequence(problem),
      onDismiss: () => void dismissDriveProblem(problem)
    });
  }
  for (const job of stalled) {
    const project = activeProjects.find(item => item.id === job.projectId);

    /* The job row carries a SNAPSHOT of the name, taken at the moment it was
       requested, and nothing updates it afterwards. ATM's row was still
       announcing "Hightlights Video" days after the typo had been corrected on
       the deliverable itself — so the dashboard was reporting a spelling the
       studio had already fixed. Read the live name; keep the snapshot only as
       a fallback for a deliverable that has since been deleted. */
    const live = project?.deliverables.find(item => item.id === job.deliverableKey)?.name;
    rows.push({
      key: `stalled-${job.id}`,
      rank: 0,
      since: job.requestedAt,
      headline: `${live || job.sourceName} — transcript request stalled`,
      detail: [project?.client, job.requestedBy ? `Requested by ${job.requestedBy}` : ''].filter(Boolean).join(' · '),
      next: stalledReason(job)
    });
  }
  for (const project of activeProjects) {
    for (const item of project.deliverables) {
      /* Only genuine shared records count — prototype seed data never does. */
      const outstanding = item.changeRequests.filter(request => !request.resolved && request.shared);
      if (outstanding.length > 0) {
        /* The oldest unresolved request is what the waiting time refers to. */
        const oldest = outstanding.reduce<number | undefined>((earliest, request) => request.at && (!earliest || request.at < earliest) ? request.at : earliest, undefined);
        push(project, item, {
          rank: 1,
          since: oldest,
          headline: `${item.name} — changes requested`,
          detail: detailLine(project, [item.version, `${outstanding.length} outstanding change${outstanding.length === 1 ? '' : 's'}`]),
          next: 'Action the changes, then publish a new version'
        });
        continue;
      }

      /* An approval only lands here when a real client actually gave it. */
      if (item.status === 'Approved' && item.approvedBy) {
        push(project, item, {
          rank: 2,
          since: item.approvedAt,
          headline: `${item.name} approved`,
          detail: detailLine(project, [item.version, `Approved by ${item.approvedBy}`]),
          next: 'Ready for Portal to publish the final files'
        });
      }
    }
  }

  /* Nothing to act on, nothing on screen. */
  if (rows.length === 0) return null;

  /* Most actionable first, then longest waiting within each kind. */
  rows.sort((a, b) => a.rank - b.rank || (a.since ?? Infinity) - (b.since ?? Infinity));
  const lineClass = 'flex w-full flex-wrap items-baseline justify-between gap-x-8 gap-y-1.5 border-b border-ruleDark py-4 text-left last:border-b-0';
  return <section aria-labelledby="needs-attention" className="border border-paper/20 px-6 py-6 sm:px-8 sm:py-7" data-id="element-1057" data-mp="wxxn06iunhdi">
      <div className="flex items-baseline justify-between gap-6" data-id="element-1058" data-mp="11v67wevdtzig">
        <h2 id="needs-attention" className="label m-0 text-paper/40" data-id="element-1059" data-mp="udhtudh0lnsb">
          Needs Attention
        </h2>
        <span className="label text-paper/40" data-id="element-1060" data-mp="pdls6kvsi8fx">{rows.length}</span>
      </div>

      <ol className="m-0 mt-5 list-none border-t border-ruleDark p-0" data-id="element-1061" data-mp="keijmqglpk37">
        {rows.map(row => {
        const waiting = waitingLabel(row.since);
        const body = <>
              <span className="min-w-0" data-id="element-1062" data-mp="bdu5lvr7jjqb">
                <span className="block font-display text-lg leading-snug sm:text-xl" data-id="element-1063" data-mp="bevrywdobsnc">
                  {row.headline}
                </span>
                <span className="label-sm mt-2 block text-paper/40" data-id="element-1064" data-mp="6lkienhh8xrq">{row.detail}</span>
                {row.next && <span className="label-sm mt-1 block text-paper/30" data-id="element-1065" data-mp="1ly9gq8om3oq">{row.next}</span>}
              </span>
              {(waiting || row.onDismiss) && <span className="flex shrink-0 flex-col items-end gap-2" data-id="element-1066" data-mp="3orp5yxqsgyjw">
                  {waiting && <span className="label-sm text-paper/40" data-id="element-1067" data-mp="3juyoq738zwww">{waiting}</span>}
                  {row.onDismiss && <button type="button" onClick={row.onDismiss} className="label-sm border border-paper/20 px-3 py-1.5 text-paper/50 transition-colors duration-150 ease-out hover:border-paper/50 hover:text-paper" data-id="element-1068" data-mp="3a5i8k95vrttc">
                      Dismiss
                    </button>}
                </span>}
            </>;
        return <li key={row.key} data-id="element-1069" data-mp="1qzwzxgqdw4z">
              {row.onOpen ? <button type="button" onClick={row.onOpen} className={`${lineClass} transition-opacity duration-150 ease-out hover:opacity-70`} data-id="element-1070" data-mp="1skw80dzq396">
                  {body}
                </button> : <div className={lineClass} data-id="element-1071" data-mp="foyc552vlwlj">{body}</div>}
            </li>;
      })}
      </ol>
    </section>;
}